import { ToastCustom } from "@components";
import { useEffect, useRef, useState } from "react";
import { TruncatedText } from "./truncated-text";
import type {
	FormatOptionLabelMeta,
	InputActionMeta,
	MenuListProps,
	MenuPortalProps,
	MultiValue,
	SingleValue,
} from "react-select";
import { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "sonner";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { useCreateGroupMutation } from "@/shared/hooks";
import type { GroupResponse } from "@/shared/types";
import { autoCapitalize } from "@/utils/string-similarity";

type Option = {
	value: string;
	label: string;
};

type GroupMultiSelectProps = {
	value: number[];
	onChange: (value: number[]) => void;
	groups: GroupResponse[];
	label?: string;
	hint?: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	isLoading?: boolean;
	isMulti?: boolean;
	placeholder?: string;
	allowCreate?: boolean;
};

/**
 * Same layer as other portaled overlays above React Aria modals.
 * Tooltip must stay above the menu.
 */
const MENU_Z_INDEX = 99_999;
const PREFERRED_MENU_MAX_HEIGHT = 300;
const PREFERRED_MENU_MAX_HEIGHT_COMPACT = 150;
const MAX_INPUT_LENGTH = 100;

/** Matches base Input filled text / placeholder. */
const VALUE_TEXT_COLOR = "#111827";
const PLACEHOLDER_TEXT_COLOR = "#6b7280";

/**
 * React Aria marks portaled siblings as inert while a modal is open.
 * `data-react-aria-top-layer` must be on the *portaled* node (appendTo),
 * not on a wrapper around MenuPortal (that wrapper stays inside the modal).
 */
function GroupMenuPortal(props: MenuPortalProps<Option, boolean>) {
	return (
		<components.MenuPortal
			{...props}
			innerProps={{
				...props.innerProps,
				"data-react-aria-top-layer": "true",
			}}
		/>
	);
}

/**
 * Stop wheel/touch from bubbling to the modal scroll container so the menu list
 * can scroll while portaled above React Aria overlays.
 * Manual scroll is required because React Aria scroll-lock can preventDefault on wheel.
 */
function GroupMenuList(props: MenuListProps<Option, boolean>) {
	const listRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const element = listRef.current;

		if (!element) {
			return;
		}

		const handleWheel = (event: WheelEvent) => {
			event.stopPropagation();
			event.preventDefault();
			element.scrollTop += event.deltaY;
		};

		element.addEventListener("wheel", handleWheel, { passive: false });

		return () => {
			element.removeEventListener("wheel", handleWheel);
		};
	}, []);

	return (
		<components.MenuList
			{...props}
			innerProps={{
				...props.innerProps,
				onTouchMove: (event) => {
					event.stopPropagation();
				},
			}}
			innerRef={(node) => {
				listRef.current = node;

				const { innerRef } = props;

				if (typeof innerRef === "function") {
					innerRef(node);
					return;
				}

				if (innerRef) {
					innerRef.current = node;
				}
			}}
		/>
	);
}

function formatGroupOptionLabel(
	option: Option,
	{ context }: FormatOptionLabelMeta<Option>
) {
	if (context === "menu") {
		return (
			<TruncatedText
				className="cursor-inherit cursor-pointer"
				tabIndex={-1}
				text={option.label}
				tooltipMaxWidth={320}
			/>
		);
	}

	return option.label;
}

function getPreferredMenuMaxHeight(): number {
	if (typeof window === "undefined") {
		return PREFERRED_MENU_MAX_HEIGHT;
	}

	return window.innerHeight < 600
		? PREFERRED_MENU_MAX_HEIGHT_COMPACT
		: PREFERRED_MENU_MAX_HEIGHT;
}

export const GroupMultiSelect = ({
	value,
	onChange,
	groups,
	label,
	hint,
	isInvalid,
	isDisabled = false,
	isLoading = false,
	isMulti = true,
	placeholder = "Selecione os grupos",
	allowCreate = true,
}: GroupMultiSelectProps) => {
	const isCreatingRef = useRef(false);
	const createGroupMutation = useCreateGroupMutation();
	const [createdGroups, setCreatedGroups] = useState<GroupResponse[]>([]);
	const [inputValue, setInputValue] = useState("");
	const preferredMenuMaxHeight = getPreferredMenuMaxHeight();

	const mergedGroups = (() => {
		if (createdGroups.length === 0) {
			return groups;
		}

		const merged = [...groups];

		for (const created of createdGroups) {
			if (!merged.some((group) => group.id === created.id)) {
				merged.push(created);
			}
		}

		return merged;
	})();

	const options: Option[] = mergedGroups.map((group) => ({
		value: String(group.id),
		label: group.name,
	}));

	const selectedOptions = value
		.map((id) => options.find((option) => Number(option.value) === id))
		.filter((option): option is Option => Boolean(option));

	const selectedValue = isMulti
		? selectedOptions
		: (selectedOptions[0] ?? null);

	const isBusy = isDisabled || isLoading || createGroupMutation.isPending;

	const getControlBackgroundColor = (isControlDisabled: boolean): string => {
		if (isInvalid) {
			return "#fef2f2";
		}

		if (isControlDisabled) {
			return "#f9fafb";
		}

		return "#ffffff";
	};

	const commitIds = (ids: number[]) => {
		onChange([...new Set(ids)]);
	};

	const handleCreateOption = async (newGroupName: string) => {
		const trimmed = autoCapitalize(newGroupName.slice(0, MAX_INPUT_LENGTH));

		if (!trimmed || isCreatingRef.current || createGroupMutation.isPending) {
			return;
		}

		isCreatingRef.current = true;

		try {
			const created = await createGroupMutation.mutateAsync(trimmed);
			setCreatedGroups((previous) => {
				if (previous.some((group) => group.id === created.id)) {
					return previous;
				}

				return [...previous, created];
			});
			commitIds(isMulti ? [...value, created.id] : [created.id]);
			setInputValue("");
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Não foi possível criar o grupo. Tente novamente.";

			toast(
				ToastCustom({
					title: "Erro ao criar grupo",
					description: message,
					variant: "error",
				})
			);
		} finally {
			isCreatingRef.current = false;
		}
	};

	const handleInputChange = (newValue: string, { action }: InputActionMeta) => {
		if (action !== "input-change") {
			setInputValue(newValue);
			return newValue;
		}

		const limited = newValue.slice(0, MAX_INPUT_LENGTH);
		setInputValue(limited);
		return limited;
	};

	const handleChange = (opts: MultiValue<Option> | SingleValue<Option>) => {
		if (isMulti) {
			const selected = (opts as MultiValue<Option>) ?? [];
			commitIds(selected.map((option) => Number(option.value)));
			return;
		}

		const selected = opts as SingleValue<Option>;
		commitIds(selected ? [Number(selected.value)] : []);
	};

	return (
		<div className="flex w-full min-w-0 flex-col gap-1.5">
			{!!label && <Label>{label}</Label>}

			<CreatableSelect<Option, boolean>
				className="w-full"
				components={{
					MenuList: GroupMenuList,
					MenuPortal: GroupMenuPortal,
				}}
				formatCreateLabel={(inputValue) => `Criar grupo "${inputValue}"`}
				formatOptionLabel={formatGroupOptionLabel}
				inputValue={inputValue}
				isClearable
				isDisabled={isBusy}
				isLoading={isLoading || createGroupMutation.isPending}
				isMulti={isMulti}
				isValidNewOption={(newOptionValue) => {
					if (!allowCreate) {
						return false;
					}

					const query = newOptionValue.trim().toLowerCase();
					if (!query || query.length > MAX_INPUT_LENGTH) {
						return false;
					}

					return !options.some(
						(option) => option.label.toLowerCase() === query
					);
				}}
				maxMenuHeight={preferredMenuMaxHeight}
				menuPlacement="auto"
				menuPortalTarget={
					typeof document === "undefined" ? null : document.body
				}
				menuPosition="fixed"
				menuShouldScrollIntoView={false}
				minMenuHeight={80}
				noOptionsMessage={() => "Nenhum grupo encontrado"}
				onChange={handleChange}
				onInputChange={handleInputChange}
				{...(allowCreate ? { onCreateOption: handleCreateOption } : {})}
				options={options}
				placeholder={placeholder}
				styles={{
					control: (base, state) => ({
						...base,
						backgroundColor: getControlBackgroundColor(state.isDisabled),
						borderColor: isInvalid ? "#ef4444" : "#d1d5db",
						borderRadius: "0.5rem",
						boxShadow: state.isFocused
							? isInvalid
								? "0 0 0 1px #f04438"
								: "0 0 0 1px #1570ef"
							: "none",
						cursor: state.isDisabled ? "not-allowed" : "default",
						minHeight: 40,
						opacity: state.isDisabled ? 0.6 : 1,
					}),
					placeholder: (base) => ({
						...base,
						color: PLACEHOLDER_TEXT_COLOR,
					}),
					input: (base) => ({
						...base,
						color: VALUE_TEXT_COLOR,
						margin: 0,
						padding: 0,
					}),
					singleValue: (base) => ({
						...base,
						color: VALUE_TEXT_COLOR,
						fontWeight: 400,
						maxWidth: "100%",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}),
					option: (base, { isFocused, isSelected }) => ({
						...base,
						backgroundColor: isFocused || isSelected ? "#e5e7eb" : "#ffffff",
						color: VALUE_TEXT_COLOR,
						cursor: "pointer",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}),
					multiValueLabel: (base) => ({
						...base,
						color: VALUE_TEXT_COLOR,
						maxWidth: 160,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}),
					menu: (base) => ({
						...base,
						borderRadius: "0.5rem",
						overflow: "hidden",
					}),
					menuPortal: (base) => ({
						...base,
						pointerEvents: "auto",
						zIndex: MENU_Z_INDEX,
					}),
					menuList: (base) => ({
						...base,
						// Keep MenuPlacer maxHeight so auto placement can flip/shrink.
						overflowY: "auto",
						overscrollBehavior: "contain",
						WebkitOverflowScrolling: "touch",
						paddingTop: 4,
						paddingBottom: 4,
					}),
				}}
				value={selectedValue}
			/>

			{!!hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
		</div>
	);
};
